import React, { useState, useEffect, useRef } from "react";
import { object, array, bool, string, number, func } from "prop-types";
import { SocialShare, Link } from "@quintype/components";
import { connect } from "react-redux";
import get from "lodash/get";
import assetify from "@quintype/framework/assetify";

import LiveText from "../../atoms/LiveText";
import Headline from "../../atoms/Headline";
import StoryCard from "../../story-templates/StoryCard";
import RelatedStories from "../RelatedStories";
import StoryTag from "../../story-templates/StoryTag";
import MetypeCommentWrapper from "../../MetypeComments";
import SocialShareTemplate from "../SocialShareTemplate";
import ScrollSnap from "../../atoms/ScrollSnap";
import LeftArrowButton from "../../atoms/icons/left-arrow-button";
import RightArrowButton from "../../atoms/icons/right-arrow-button";
import PhotoRelatedStories from "../PhotoRelatedStories";
import Icon from "../../icons";
import { toBanglaNum, getStoryDate, getKeyHighlights } from "../../utils/utils";
import StoryPageMetaData from "../StoryPageMetaData";
import DfpAd from "../../dfp-ad";
import { setMetypeBar } from "../../helper/actions";
import { staticTexts } from "../../utils/Translate";
import LightBox from "../LightBox";
import BookmarkViewIcon from "../../atoms/BookmarkViewIcon";
import FullScreenIcon from "../../../../assets/icons/full-screen-icon.png";
import { Share } from "../../atoms/icons/StoryPageIcons";
import ShareAllTemplate from "../ShareAllTemplate";
import { MetypeReactionsWidget } from "../../atoms/metype-react";
import Loadericon from "../../atoms/load-more-button-loader";
import KeyHighlights from "../../molecules/KeyHighlights";
import StoryAds from "../StoryAds";

import "./liveStoryContent.m.css";

const LiveStoryContent = ({
  story = null,
  slicedStoryCards = [],
  metypeConfig = null,
  isLiveBlog = false,
  checkKeyElements = false,
  imageElements = null,
  isPhotoStory = false,
  sections,
  domainSlug,
  setMetypeBar,
  isMetypeBar,
  publisherAttributes,
  isMobile,
  jwtToken,
  authHost,
  bottomAd,
  bottomAdPresence,
  member,
  storyFromReducer,
  scrollIndex,
  datatrackingcategory = "",
  datatrackingaction = "",
  isDesktop,
}) => {
  const [showCarousel, setShowCarousel] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lowerBottomAd, setLowerBottomAd] = useState(false);
  const [isSocialShare, setSocialShare] = useState(false);
  const ref = useRef(null);
  const metypeRef = useRef(null);
  const [metypeTarget, setMetypeTarget] = useState(null);
  const [relStories, setRelStories] = useState([]);
  const subHeadlineStyle = `${publisherAttributes.lang}-sub-headline`;
  const keyHighlights = getKeyHighlights(story.cards);

  const selectionChange = (index) => setSelectedIndex(index);
  const sectionName = get(story, ["sections", "0"], "");
  const firstStorySeo = get(story, ["seo", "meta-keywords", "0"], null);
  const requiredSectionIds = sections.filter((item) => item["domain-slug"] === domainSlug).map((item) => item.id);
  const sectionIds = requiredSectionIds.length > 0 ? requiredSectionIds.toString() : "";

  useEffect(() => {
    if (story["story-template"] === "live-blog") {
      global.deviceWidth < 1264 && setLowerBottomAd(!lowerBottomAd);
    }
    if (story.tags.length > 0) {
      fetch(
        `/api/v1/advanced-search?tag-name=${decodeURIComponent(firstStorySeo)}&not-story-content-ids=${
          story.id
        }&limit=4&sort=latest-published&section-id=${sectionIds}`
      )
        .then(function (res) {
          // Examine the text in the response
          res.json().then(function (data) {
            setRelStories(data.items);
          });
        })
        .catch(function (err) {
          console.log("Fetch Error :-S", err);
        });
    } else {
      setRelStories([]);
    }
    setMetypeTarget(story.id);
    setMetypeBar(true);
  }, [story]);

  const children =
    imageElements && imageElements.length > 0
      ? imageElements.map((item, index) => <LightBox element={item} isCarousel={true} key={index} />)
      : null;

  const closeEvent = (e) => {
    setShowCarousel(!showCarousel);

    exitFullscreenWindow();
  };

  const adTitle = staticTexts(publisherAttributes.lang).advertising;

  const renderingMeTypeBar = () =>
    !!(isMetypeBar && metypeTarget === get(metypeRef, ["current", "classList", "value"], ""));

  if (typeof document !== "undefined") {
    renderingMeTypeBar();
  }

  const toggleShare = () => setSocialShare(!isSocialShare);

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setSocialShare(false);
    }
  };

  useEffect(() => {
    if (global.deviceWidth > 876) {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }
  });

  const exitFullscreenWindow = () => {
    if (document.fullscreenElement) {
      document
        .exitFullscreen()
        .then(() => console.log("exited form Full Screen mode"))
        .catch((err) => console.error(err));
    }
  };

  const makeFullScreen = () =>
    document.fullscreenElement ? exitFullscreenWindow() : document.documentElement.requestFullscreen();

  return (
    <React.Fragment>
      <div styleName="live-story-content">
        <div>
          {isDesktop && keyHighlights.length > 0 && (
            <div styleName="key-highlights-wrapper">
              <KeyHighlights keyHighlights={keyHighlights} />
            </div>
          )}
        </div>
        <div className={`story-content ${!checkKeyElements ? "no-key-elements" : ""}`}>
          {showCarousel && (
            <div styleName="scroll-snap">
              <ScrollSnap
                leftArrow={<LeftArrowButton />}
                rightArrow={<RightArrowButton />}
                selectionChange={selectionChange}
              >
                {children}
              </ScrollSnap>
              <div styleName="icons">
                <span styleName="image-index">{`${
                  publisherAttributes.lang === "en" ? selectedIndex + 1 : toBanglaNum(selectedIndex + 1)
                }/${
                  publisherAttributes.lang === "en" ? imageElements.length : toBanglaNum(imageElements.length)
                }`}</span>
                <img src={`${assetify(FullScreenIcon)}`} styleName="full-screen-icon" onClick={makeFullScreen}></img>
                <span onClick={() => closeEvent()}>
                  <Icon type="close" className="close" />
                </span>
              </div>
            </div>
          )}
          <div>
            <div styleName="headline">
              <LiveText story={story} lang={publisherAttributes.lang} />
              {story.subheadline && (
                <Headline
                  text={story.subheadline}
                  headlineType={16}
                  headerLevel={2}
                  className={`story-sub-headline`}
                  styleName={subHeadlineStyle}
                />
              )}
              <Headline
                text={story.headline}
                headlineType={9}
                headerLevel={1}
                className={`story-headline ${publisherAttributes.lang}-story-headline`}
              />
            </div>
            <StoryPageMetaData story={story} lang={publisherAttributes.lang} />
            {slicedStoryCards.length > 0 &&
              slicedStoryCards.map((card, index) => (
                <div key={card.id} id={card.id}>
                  {isLiveBlog && (
                    <React.Fragment>
                      <div styleName="live-blog-card-wrapper" className="live-blog-card">
                        <div styleName={`time-wrapper ${publisherAttributes.lang === "en" ? "en-time-wrapper" : ""}`}>
                          {getStoryDate(card["card-added-at"], publisherAttributes.lang, "", story["story-template"])}
                        </div>
                        <div styleName="story-card-wrapper">
                          <StoryCard
                            card={card}
                            story={story}
                            id={`#card-${card.id}`}
                            isLiveBlog={isLiveBlog}
                            publisherAttributes={publisherAttributes}
                            domainSlug={domainSlug}
                          />
                        </div>
                      </div>
                      <StoryAds
                        slicedStoryCards={slicedStoryCards}
                        index={index}
                        adTitle={adTitle}
                        story={story}
                        scrollIndex={scrollIndex}
                        isMobile={isMobile}
                        isLiveBlog={isLiveBlog}
                      />
                    </React.Fragment>
                  )}
                </div>
              ))}
          </div>
          {story.tags && story.tags.length > 0 && (
            <div styleName="tags wrapper" className="print-tags">
              {sectionName && (
                <Link
                  href={sectionName["section-url"]}
                  styleName="sub-section-name"
                  data-tracking-category={datatrackingcategory}
                  data-tracking-action={datatrackingaction}
                  data-tracking-label={sectionName["display-name"]}
                >
                  {publisherAttributes.lang === "en"
                    ? story["story-template"] === "photo"
                      ? "Watch more from "
                      : "Read more from "
                    : ""}
                  <span styleName="section-name">{sectionName["display-name"]}</span>
                  {publisherAttributes.lang === "bn"
                    ? story["story-template"] === "photo"
                      ? " থেকে আরও দেখুন"
                      : " থেকে আরও পড়ুন"
                    : ""}
                </Link>
              )}
              <StoryTag tags={story.tags} />
            </div>
          )}
        </div>
        <div>
          {isDesktop && (
            <div styleName="r1-ad">
              <DfpAd
                adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_336x280_R1" : "News_336x280_R1"}
                targetStory={story}
                slotId={`story_${scrollIndex}-r1`}
              />
            </div>
          )}
        </div>
      </div>
      <div styleName="comments-b1-ads-wrapper">
        <div></div>
        <div>
          <div styleName="metype-reactions-with-comments" className="print-metype-reactions-with-comments">
            {publisherAttributes.showMetypeReactions && (
              <MetypeReactionsWidget
                host={metypeConfig.metypeHost}
                accountId={metypeConfig.metypeAccountId}
                storyUrl={story.url}
                storyId={story.id}
              />
            )}
            <div styleName={`metype-wrapper wrapper ${publisherAttributes.showMetype ? "" : "no-metype"}`}>
              {publisherAttributes.showMetype && (
                <div styleName="commment-text">{staticTexts(publisherAttributes.lang).doComment}</div>
              )}

              <div styleName="bottom-icons">
                <SocialShare template={SocialShareTemplate} title={story.headline} fullUrl={encodeURI(story.url)} />
                <span styleName="share-button" ref={ref}>
                  <span onClick={toggleShare}>
                    <Share color1="#eee" color2="#222" />
                  </span>
                  {isSocialShare && (
                    <SocialShare
                      template={ShareAllTemplate}
                      title={story.headline}
                      fullUrl={encodeURI(story.url)}
                      toggleShare={toggleShare}
                    />
                  )}
                </span>
                {publisherAttributes.showBookmark && (
                  <span styleName="bookmark">
                    <BookmarkViewIcon story={story} />
                  </span>
                )}
              </div>
            </div>
          </div>
          <div ref={metypeRef} className={storyFromReducer.id}>
            {publisherAttributes.showMetype && renderingMeTypeBar() ? (
              <MetypeCommentWrapper
                metypeConfig={metypeConfig}
                storyUrl={storyFromReducer.url}
                metype={isMetypeBar}
                jwtToken={jwtToken}
                authHost={authHost}
                lang={publisherAttributes.lang}
                member={member}
              />
            ) : publisherAttributes.showMetype ? (
              <Loadericon color="#ed1c24" width="44" height="44" />
            ) : null}
          </div>
        </div>
        <div styleName={`adslot adslot-r2`} className="print-adslot">
          <div styleName="advertising">{adTitle}</div>
          <DfpAd
            adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_300x250_R2" : "News_336x280_R2"}
            targetStory={story}
            slotId={`story_${scrollIndex}-R2`}
          />
        </div>
      </div>
      <div styleName={`adslot adslot-728x90`} className="print-adslot">
        <div styleName="advertising">{adTitle}</div>
        <DfpAd
          adtype={publisherAttributes.lang === "en" ? "EnPalo_ROS_728x90_B2" : "News_728x90_B2"}
          targetStory={story}
          slotId={`story_${scrollIndex}-B2`}
        />
      </div>
      {relStories.length > 0 && (
        <div
          styleName="related-stories-wrapper wrapper"
          className="print-related-stories-wrapper related-stories-wrapper-2 "
        >
          {!isPhotoStory && <RelatedStories stories={relStories} isMobile={isMobile} />}
          {isPhotoStory && <PhotoRelatedStories relatedStories={relStories.slice(0, 3)} />}
        </div>
      )}
    </React.Fragment>
  );
};

LiveStoryContent.propTypes = {
  story: object.isRequired,
  metypeConfig: object,
  slicedStoryCards: array,
  isLiveBlog: bool,
  checkKeyElements: bool,
  imageElements: array,
  isPhotoStory: bool,
  sections: array,
  domainSlug: string,
  isMetypeBar: bool,
  setMetypeBar: func,
  publisherAttributes: object,
  isMobile: bool,
  jwtToken: string,
  authHost: string,
  bottomAd: bool,
  bottomAdPresence: bool,
  member: object,
  storyFromReducer: object,
  customAds: object,
  scrollIndex: number,
  datatrackingcategory: string,
  datatrackingaction: string,
  isDesktop: bool,
};

const mapStateToProps = (state) => ({
  member: state.member,
  sections: get(state, ["qt", "config", "sections"]),
  domainSlug: get(state, ["qt", "config", "domainSlug"], null),
  metypeConfig: get(state, ["qt", "config", "publisher-attributes", "metypeConfig"], {}),
  isMetypeBar: get(state, ["metypeReducer", "isMetypeBar"], true),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  bottomAdPresence: get(state, ["breakingNewsAdReducer", "adPresence"], false),
  bottomAd: get(state, ["TopAndBottomAdReducer", "showAd"], false),
  storyFromReducer: get(state, ["headerReducer", "showStoryHeader"], {}),
});

const mapDispatchToProps = (dispatch) => ({
  setMetypeBar: (flag) => dispatch(setMetypeBar(flag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LiveStoryContent);
